import React, { useState } from 'react';

import { Box } from '@mui/material';

import { CommonComponentProps, ImageObject } from '../../pages/types';
import ImageFullscreen from './image-fullscreen';

interface ImageGalleryProps extends CommonComponentProps {
  images: ImageObject[];
  rowHeight: string | number;
  columns?: number; // Number of columns on desktop
  minColumns?: number; // Minimum columns on mobile (overrides responsive behavior)
  gap?: string; // Gap between images
}

const ImageGallery: React.FC<ImageGalleryProps> = ({
  id,
  images,
  rowHeight,
  columns = 3, // Default to 3 columns
  minColumns, // No default - optional override
  gap = '8px', // Default gap
  sx,
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  // Calculate responsive columns
  const tabletColumns = Math.max(1, Math.floor(columns / 2)); // Half of desktop columns
  const mobileColumns = minColumns || 1; // Use minColumns if specified, otherwise 1

  return (
    <Box
      id={id}
      sx={{
        py: '10px',
        display: 'grid',
        gridTemplateColumns: {
          xs: `repeat(${mobileColumns}, 1fr)`, // 1 column on mobile
          sm: `repeat(${tabletColumns}, 1fr)`, // Half columns on tablet
          md: `repeat(${columns}, 1fr)`, // Full columns on desktop
        },
        gridAutoRows: rowHeight, // Set the height of the rows
        gap: gap, // Use configurable gap
        ...sx,
      }}
    >
      {images.map((image, index) => (
        <ImageFullscreen
          firstImage={image}
          images={images}
          currentIndex={currentIndex}
          setCurrentIndex={setCurrentIndex}
          key={index}
        >
          <Box
            component="img"
            src={image.url || ''}
            alt={image.altText || `Image ${index + 1}`}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              aspectRatio: '9/9',
              cursor: 'pointer',
            }}
            onClick={() => setCurrentIndex(index)}
          />
        </ImageFullscreen>
      ))}
    </Box>
  );
};

export default ImageGallery;
